import { toDate } from '../../../shared/utils/dateHelpers';
import { isIgnition2025, RawClient } from './clientRecord';

/**
 * The Ignition and Foundation alert rules, moved out of the old Dashboard
 * component unchanged. Reason strings are kept byte-for-byte: cleared alerts
 * are stored as these strings, so any rewording would resurface them.
 */

export interface ReportFlags {
  initial: boolean;
  review: boolean;
  foundationInitial: boolean;
  foundationReview: boolean;
}

export interface Submission {
  uploadCategory?: string;
  report_type: 'Initial' | 'Review';
  formData?: Record<string, unknown>;
}

const DAY_MS = 24 * 60 * 60 * 1000;
const WEEK_MS = 7 * DAY_MS;

/** report_status still allows an "initial report missing" alert (blank, or names neither stage). */
const statusAllowsInitial = (status: RawClient['report_status']) =>
  status === undefined ||
  status === null ||
  status === '' ||
  (typeof status === 'string' && !status.includes('Initial') && !status.includes('Review'));

const statusAllowsReview = (status: RawClient['report_status']) =>
  status === undefined || status === null || status === '' || (typeof status === 'string' && !status.includes('Review'));

const categoriesOf = (submissions: Submission[]) => submissions.map((submission) => submission.uploadCategory);

/**
 * Ignition 2025 alert reasons. `reports` and `submissions` are undefined while
 * they load (or after they fail); the rules that need them are skipped rather
 * than guessed.
 */
export function ignitionAlertReasons(
  raw: RawClient,
  now: Date,
  reports: ReportFlags | undefined,
  submissions: Submission[] | undefined
): string[] {
  if (!raw.ignition_expiration_date || !isIgnition2025(raw)) return [];
  const expiration = toDate(raw.ignition_expiration_date);
  if (!expiration) return [];

  const reasons: string[] = [];
  const weeksUntilExpiration = Math.round((expiration.getTime() - now.getTime()) / WEEK_MS);
  const first = toDate(raw.first_ignition_session_date);
  const weeksSinceFirstSession = first ? Math.floor((now.getTime() - first.getTime()) / WEEK_MS) : 0;
  const nutrition = raw.nutrition_sessions || 0;
  const metabolic = raw.metabolic_screenings || 0;
  const performance = raw.physical_performance_screenings || 0;

  if (weeksSinceFirstSession >= 2 && weeksUntilExpiration > 0) {
    if (nutrition === 0) reasons.push('0 nutrition sessions redeemed [At 2 weeks]');
    if (metabolic === 0) reasons.push('0 metabolic sessions redeemed [At 2 weeks]');
    if (performance === 0) reasons.push('0 performance screening sessions redeemed [At 2 weeks]');
    if (reports && !reports.initial && statusAllowsInitial(raw.report_status)) {
      reasons.push('Initial report not generated [At 2 weeks]');
    }
  }

  if (weeksSinceFirstSession >= 6 && weeksUntilExpiration > 0 && nutrition <= 1) {
    reasons.push(`${nutrition} nutrition sessions redeemed [Less than 6 weeks remaining]`);
  }

  if (weeksUntilExpiration <= 0) {
    if (nutrition <= 2) reasons.push(`${nutrition} nutrition sessions redeemed [Less than 1 week remaining]`);
    if (metabolic <= 1) reasons.push(`${metabolic} metabolic sessions redeemed [Less than 1 week remaining]`);
    if (performance <= 1) reasons.push(`${performance} performance screening sessions redeemed [Less than 1 week remaining]`);
    if (reports && !reports.review && statusAllowsReview(raw.report_status)) {
      reasons.push('Review report not generated [Less than 1 week remaining]');
    }
  }

  if (submissions) {
    const submitted = categoriesOf(submissions);
    const has = (category: string) => submitted.includes(category);
    if (nutrition > 0 && !has('Nutrition Plan')) reasons.push('Nutrition Initial Assessment Submission Outstanding');
    if (metabolic > 0 && !has('Metabolic Initial Assessment')) reasons.push('Metabolic Initial Assessment Submission Outstanding');
    if (performance > 0 && !has('Movement Initial Assessment')) {
      reasons.push('Physical Performance Initial Assessment Submission Outstanding');
    }
    if (has('Nutrition Plan') && has('Metabolic Initial Assessment') && has('Movement Initial Assessment')) {
      if (!has('InBody Scan')) reasons.push('InBody Initial Assessment Submission Outstanding');
      if (!has('Training Plan')) reasons.push('Training Plan Initial Submission Outstanding');
    }
    if (nutrition > 2 && !has('Nutrition Review')) reasons.push('Nutrition Review Assessment Submission Outstanding');
    if (metabolic > 1 && !has('Metabolic Review Assessment')) reasons.push('Metabolic Review Assessment Submission Outstanding');
    if (performance > 1 && !has('Movement Review Assessment')) {
      reasons.push('Physical Performance Review Assessment Submission Outstanding');
    }
    if (has('Nutrition Review') && has('Metabolic Review Assessment') && has('Movement Review Assessment')) {
      const reviewInBody = submissions.some((s) => s.uploadCategory === 'InBody Scan' && s.report_type === 'Review');
      if (!reviewInBody) reasons.push('InBody Review Assessment Submission Outstanding');
      if (!has('Training Review')) reasons.push('Training Review Assessment Submission Outstanding');
    }
  }

  return reasons;
}

const INITIAL_REPORT_CATEGORIES = ['Nutrition Plan', 'Metabolic Initial Assessment', 'Movement Initial Assessment', 'InBody Scan', 'Training Plan'];
const REVIEW_REPORT_CATEGORIES = ['Nutrition Review', 'Metabolic Review Assessment', 'Movement Review Assessment', 'Training Review'];

/** Ignition reports whose assessments are all in but which have not been generated. */
export function ignitionPendingReports(
  raw: RawClient,
  reports: ReportFlags,
  submissions: Submission[]
): { initial: boolean; review: boolean } {
  if (!isIgnition2025(raw) || !toDate(raw.first_ignition_session_date)) return { initial: false, review: false };
  const submitted = categoriesOf(submissions);
  const allInitial = INITIAL_REPORT_CATEGORIES.every((category) => submitted.includes(category));
  const allReview =
    REVIEW_REPORT_CATEGORIES.every((category) => submitted.includes(category)) &&
    submissions.some((s) => s.uploadCategory === 'InBody Scan' && s.report_type === 'Review');
  const statusSaysInitial = typeof raw.report_status === 'string' && raw.report_status.includes('Initial');
  return {
    initial: !reports.initial && allInitial && statusAllowsInitial(raw.report_status),
    review: !reports.review && allReview && (reports.initial || statusSaysInitial),
  };
}

const FOUNDATION_STAGE_FIELD = 'Assessment Stage (Foundation)';

/** Foundation alert reasons and pending-report flags; same loading contract as {@link ignitionAlertReasons}. */
export function foundationAlerts(
  raw: RawClient,
  now: Date,
  reports: ReportFlags | undefined,
  submissions: Submission[] | undefined
): { reasons: string[]; pendingInitial: boolean; pendingReview: boolean } {
  const none = { reasons: [], pendingInitial: false, pendingReview: false };
  const start = toDate(raw.first_foundation_session_date);
  const expiration = toDate(raw.foundation_expiration_date);
  if (!start || !expiration) return none;

  const reasons: string[] = [];
  const daysSinceStart = Math.floor((now.getTime() - start.getTime()) / DAY_MS);
  const daysUntilExpiration = Math.floor((expiration.getTime() - now.getTime()) / DAY_MS);
  const alliedHealth = raw.foundation_allied_health_sessions || 0;
  const vald = raw.foundation_physical_performance_sessions || 0;
  const nutrition = raw.foundation_nutrition_sessions || 0;

  const atStage = (stage: 'Initial' | 'Review', category: string) =>
    (submissions ?? []).some((s) => s.formData?.[FOUNDATION_STAGE_FIELD] === stage && s.uploadCategory === category);
  const initial = {
    nutrition: atStage('Initial', 'Nutrition Plan'),
    training: atStage('Initial', 'Training Plan'),
    inbody: atStage('Initial', 'InBody Scan'),
    movement: atStage('Initial', 'Movement Initial Assessment'),
    alliedHealth: atStage('Initial', 'Allied Health Assessment'),
  };
  const review = {
    nutrition: atStage('Review', 'Nutrition Review'),
    training: atStage('Review', 'Training Review'),
    inbody: atStage('Review', 'InBody Scan'),
    movement: atStage('Review', 'Movement Review Assessment'),
  };

  if (daysSinceStart >= 14 && expiration > now) {
    if (alliedHealth === 0) reasons.push('0 allied health sessions redeemed [At 2 weeks]');
    if (vald === 0) reasons.push('0 physical performance (Vald) sessions redeemed [At 2 weeks]');
    if (nutrition === 0) reasons.push('0 nutrition sessions redeemed [At 2 weeks]');
    if (reports && !reports.foundationInitial) reasons.push('Initial report not generated [At 2 weeks]');
  }

  // Persists after expiration, as it always has: these clear only when someone clears them.
  if (daysUntilExpiration <= 7) {
    if (vald < 2) reasons.push(`${vald} physical performance (Vald) sessions redeemed [Less than 1 week remaining]`);
    if (nutrition < 2) reasons.push(`${nutrition} nutrition sessions redeemed [Less than 1 week remaining]`);
    if (reports && !reports.foundationReview) reasons.push('Review report not generated [Less than 1 week remaining]');
  }

  if (submissions) {
    if (initial.nutrition && !initial.alliedHealth) reasons.push('Allied Health Initial Assessment Submission Outstanding');
    if (initial.nutrition && !initial.inbody) reasons.push('Inbody Initial Assessment Submission Outstanding');
    if (initial.nutrition && !initial.movement) reasons.push('Movement Initial Assessment Submission Outstanding');
    if (initial.nutrition && !initial.training) reasons.push('Training Plan Initial Submission Outstanding');
    if (review.nutrition && !review.inbody) reasons.push('Inbody Review Assessment Submission Outstanding');
    if (review.nutrition && !review.movement) reasons.push('Movement Review Assessment Submission Outstanding');
    if (review.nutrition && !review.training) reasons.push('Training Review Assessment Submission Outstanding');
  }

  const known = Boolean(reports && submissions);
  return {
    reasons,
    pendingInitial:
      known && initial.nutrition && initial.training && initial.inbody && initial.movement && initial.alliedHealth && !reports!.foundationInitial,
    pendingReview: known && review.nutrition && review.training && review.inbody && review.movement && !reports!.foundationReview,
  };
}
